import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";
import { 
  FileText, 
  Download, 
  ArrowLeft,
  Pen,
  RotateCcw,
  Check,
  Trophy,
  Users,
  Gift,
  Calendar,
  Clock,
  Building2,
  Shield,
  Loader2,
  AlertTriangle
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { drawsAPI } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { getImageUrl } from "../lib/utils";

export default function DrawCertificate() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [draw, setDraw] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const certificateRef = useRef(null);
  const signatureCanvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  
  // Certificate data
  const [certificateData, setCertificateData] = useState({
    supervisor_type: "controleur", // "huissier" or "controleur"
    supervisor_name: "",
    supervisor_role: "",
    supervisor_date: new Date().toISOString().split('T')[0],
    supervisor_time: new Date().toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }),
    notes: "",
    signature: ""
  });

  useEffect(() => {
    loadDraw();
  }, [id]);

  const loadDraw = async () => {
    try {
      const res = await drawsAPI.get(id);
      setDraw(res.data);
      
      // Pre-fill with draw's controller info if available
      if (res.data.bailiff_name || res.data.bailiff_info) {
        setCertificateData(prev => ({
          ...prev,
          supervisor_type: res.data.has_bailiff ? "huissier" : "controleur",
          supervisor_name: res.data.bailiff_name || "",
          supervisor_role: res.data.bailiff_info || ""
        }));
      }
    } catch (error) {
      toast.error("Tirage non trouvé");
      navigate("/dashboard");
    } finally {
      setLoading(false);
    }
  };

  // Clear signature
  const clearSignature = () => {
    const canvas = signatureCanvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      setCertificateData(prev => ({ ...prev, signature: "" }));
    }
  };

  // Mouse drawing handlers
  const handleMouseDown = (e) => {
    setIsDrawing(true);
    const canvas = signatureCanvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const ctx = canvas.getContext('2d');
    ctx.beginPath();
    ctx.moveTo(
      (e.clientX - rect.left) * (canvas.width / rect.width),
      (e.clientY - rect.top) * (canvas.height / rect.height)
    );
  };

  const handleMouseMove = (e) => {
    if (!isDrawing) return;
    const canvas = signatureCanvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const ctx = canvas.getContext('2d');
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.strokeStyle = '#1e3a8a';
    ctx.lineTo(
      (e.clientX - rect.left) * (canvas.width / rect.width),
      (e.clientY - rect.top) * (canvas.height / rect.height)
    );
    ctx.stroke();
  };

  const handleMouseUp = () => {
    setIsDrawing(false);
    saveSignature();
  };

  // Touch drawing handlers
  const handleTouchStart = (e) => {
    e.preventDefault();
    setIsDrawing(true);
    const canvas = signatureCanvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const touch = e.touches[0];
    const ctx = canvas.getContext('2d');
    ctx.beginPath();
    ctx.moveTo(
      (touch.clientX - rect.left) * (canvas.width / rect.width),
      (touch.clientY - rect.top) * (canvas.height / rect.height)
    );
  };

  const handleTouchMove = (e) => {
    e.preventDefault();
    if (!isDrawing) return;
    const canvas = signatureCanvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const touch = e.touches[0];
    const ctx = canvas.getContext('2d');
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.strokeStyle = '#1e3a8a';
    ctx.lineTo(
      (touch.clientX - rect.left) * (canvas.width / rect.width),
      (touch.clientY - rect.top) * (canvas.height / rect.height)
    );
    ctx.stroke();
  };

  const handleTouchEnd = (e) => {
    e.preventDefault();
    setIsDrawing(false);
    saveSignature();
  };

  const saveSignature = () => {
    const canvas = signatureCanvasRef.current;
    if (canvas) {
      setCertificateData(prev => ({ ...prev, signature: canvas.toDataURL('image/png') }));
    }
  };

  // Download PDF
  const downloadPDF = async () => {
    if (!certificateData.supervisor_name || !certificateData.signature) {
      toast.error("Veuillez remplir le nom du contrôleur et apposer une signature");
      return;
    }

    setGenerating(true);
    try {
      const element = certificateRef.current;
      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        backgroundColor: '#ffffff'
      });
      
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = (canvas.height * pdfWidth) / canvas.width;
      
      pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
      pdf.save(`certificat_${draw?.title?.replace(/\s+/g, '_') || 'tirage'}_${new Date().toISOString().split('T')[0]}.pdf`);
      
      toast.success("Certificat PDF téléchargé !");
    } catch (error) {
      console.error("PDF generation error:", error);
      toast.error("Erreur lors de la génération du PDF");
    } finally {
      setGenerating(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!draw) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <p className="text-muted-foreground">Tirage non trouvé</p>
      </div>
    );
  }

  if (draw.status !== "completed") {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-background gap-4">
        <AlertTriangle className="w-16 h-16 text-amber-500" />
        <h2 className="text-xl font-bold">Tirage non terminé</h2>
        <p className="text-muted-foreground">Le certificat n'est disponible qu'après l'exécution du tirage</p>
        <Button onClick={() => navigate(`/draw/${id}/edit`)}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Retour à l'éditeur
        </Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800">
      {/* Header */}
      <div className="bg-white dark:bg-slate-900 border-b shadow-sm sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate(`/draw/${id}/edit`)}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Retour
            </Button>
            <div>
              <h1 className="text-lg font-bold flex items-center gap-2">
                <FileText className="w-5 h-5 text-blue-600" />
                Certificat de Contrôle
              </h1>
              <p className="text-sm text-muted-foreground">{draw.title}</p>
            </div>
          </div>
          <Button 
            onClick={downloadPDF}
            disabled={generating || !certificateData.supervisor_name || !certificateData.signature}
            className="bg-red-600 hover:bg-red-700"
          >
            {generating ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Download className="w-4 h-4 mr-2" />
            )}
            Télécharger PDF
          </Button>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-8">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Left side - Form */}
          <div className="lg:col-span-1 space-y-6">
            {/* Supervisor Type */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Shield className="w-4 h-4 text-blue-600" />
                  Type de contrôleur
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <button
                  type="button"
                  onClick={() => setCertificateData(prev => ({ ...prev, supervisor_type: "huissier" }))}
                  className={`w-full p-3 rounded-lg border-2 text-left transition-all ${
                    certificateData.supervisor_type === "huissier" 
                      ? "border-blue-500 bg-blue-50 dark:bg-blue-950/30" 
                      : "border-border hover:border-blue-300"
                  }`}
                >
                  <p className="font-medium text-sm">Huissier de justice</p>
                  <p className="text-xs text-muted-foreground">Contrôle officiel certifié</p>
                </button>
                <button
                  type="button"
                  onClick={() => setCertificateData(prev => ({ ...prev, supervisor_type: "controleur" }))}
                  className={`w-full p-3 rounded-lg border-2 text-left transition-all ${
                    certificateData.supervisor_type === "controleur" 
                      ? "border-green-500 bg-green-50 dark:bg-green-950/30" 
                      : "border-border hover:border-green-300"
                  }`}
                >
                  <p className="font-medium text-sm">Contrôleur</p>
                  <p className="text-xs text-muted-foreground">Témoin du bon déroulement</p>
                </button>
              </CardContent>
            </Card>

            {/* Supervisor Info */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Pen className="w-4 h-4 text-green-600" />
                  Informations du contrôleur
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Nom complet *</Label>
                  <Input
                    placeholder={certificateData.supervisor_type === "huissier" ? "Me Jean Dupont" : "Jean Dupont"}
                    value={certificateData.supervisor_name}
                    onChange={(e) => setCertificateData(prev => ({ ...prev, supervisor_name: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label>{certificateData.supervisor_type === "huissier" ? "N° d'agrément / Étude" : "Fonction / Rôle"}</Label>
                  <Input
                    placeholder={certificateData.supervisor_type === "huissier" ? "Étude de Me Dupont, Paris" : "Président, Responsable..."}
                    value={certificateData.supervisor_role}
                    onChange={(e) => setCertificateData(prev => ({ ...prev, supervisor_role: e.target.value }))}
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label>Date</Label>
                    <Input
                      type="date"
                      value={certificateData.supervisor_date}
                      onChange={(e) => setCertificateData(prev => ({ ...prev, supervisor_date: e.target.value }))}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Heure</Label>
                    <Input
                      type="time"
                      value={certificateData.supervisor_time}
                      onChange={(e) => setCertificateData(prev => ({ ...prev, supervisor_time: e.target.value }))}
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Observations</Label>
                  <Textarea
                    placeholder="Notes sur le déroulement du tirage..."
                    value={certificateData.notes}
                    onChange={(e) => setCertificateData(prev => ({ ...prev, notes: e.target.value }))}
                    rows={3}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Signature */}
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Pen className="w-4 h-4 text-purple-600" />
                    Signature manuscrite *
                  </CardTitle>
                  <Button variant="ghost" size="sm" onClick={clearSignature}>
                    <RotateCcw className="w-4 h-4 mr-1" />
                    Effacer
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-xs text-muted-foreground mb-3">
                  Signez avec votre doigt ou souris ci-dessous
                </p>
                <div className="border-2 border-dashed rounded-lg bg-white overflow-hidden">
                  <canvas
                    ref={signatureCanvasRef}
                    width={400}
                    height={120}
                    className="w-full cursor-crosshair touch-none"
                    style={{ touchAction: 'none' }}
                    onMouseDown={handleMouseDown}
                    onMouseMove={handleMouseMove}
                    onMouseUp={handleMouseUp}
                    onMouseLeave={() => isDrawing && handleMouseUp()}
                    onTouchStart={handleTouchStart}
                    onTouchMove={handleTouchMove}
                    onTouchEnd={handleTouchEnd}
                  />
                </div>
                {certificateData.signature && (
                  <p className="text-xs text-green-600 flex items-center gap-1 mt-2">
                    <Check className="w-3 h-3" />
                    Signature enregistrée
                  </p>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Right side - Certificate Preview */}
          <div className="lg:col-span-2">
            <div 
              ref={certificateRef}
              className="bg-white rounded-xl shadow-xl p-8 space-y-6"
              style={{ minHeight: '800px' }}
            >
              {/* Certificate Header */}
              <div className="text-center border-b-4 border-blue-600 pb-6">
                <div className="flex items-center justify-center gap-3 mb-2">
                  {draw.organizer_logo && (
                    <img 
                      src={getImageUrl(draw.organizer_logo)} 
                      alt="" 
                      className="h-12 w-auto object-contain"
                    />
                  )}
                  <div>
                    <h1 className="text-2xl font-bold text-blue-900">CERTIFICAT DE CONTRÔLE</h1>
                    <p className="text-sm text-slate-600">Tirage au Sort</p>
                  </div>
                </div>
                <p className="text-slate-500 text-sm">
                  Document officiel généré par TIREO
                </p>
              </div>

              {/* Draw Info */}
              <div className="grid grid-cols-2 gap-6 py-4 border-b">
                <div>
                  <h3 className="font-bold text-slate-800 mb-3 flex items-center gap-2">
                    <Trophy className="w-4 h-4 text-amber-500" />
                    Informations du tirage
                  </h3>
                  <div className="space-y-2 text-sm text-slate-700">
                    <p><span className="text-slate-500">Titre :</span> <strong className="text-slate-900">{draw.title}</strong></p>
                    <p><span className="text-slate-500">Organisateur :</span> <span className="text-slate-800">{draw.organizer_name}</span></p>
                    <p><span className="text-slate-500">Date du tirage :</span> <span className="text-slate-800">{draw.draw_timestamp ? new Date(draw.draw_timestamp).toLocaleDateString('fr-FR') : "N/A"}</span></p>
                    <p><span className="text-slate-500">Heure :</span> <span className="text-slate-800">{draw.draw_timestamp ? new Date(draw.draw_timestamp).toLocaleTimeString('fr-FR') : "N/A"}</span></p>
                  </div>
                </div>
                <div>
                  <h3 className="font-bold text-slate-800 mb-3 flex items-center gap-2">
                    <Users className="w-4 h-4 text-blue-500" />
                    Statistiques
                  </h3>
                  <div className="space-y-2 text-sm text-slate-700">
                    <p><span className="text-slate-500">Participants :</span> <strong className="text-slate-900">{draw.participants?.length || 0}</strong></p>
                    <p><span className="text-slate-500">Lots à gagner :</span> <span className="text-slate-800">{draw.prizes?.length || 0}</span></p>
                    <p><span className="text-slate-500">Gagnants :</span> <strong className="text-green-600">{draw.results?.length || 0}</strong></p>
                    <p><span className="text-slate-500">Mode :</span> <span className="text-slate-800">{draw.prize_attribution_mode === "registration_order" ? "Par ordre des lots" : "Aléatoire"}</span></p>
                  </div>
                </div>
              </div>

              {/* Winners Table */}
              <div>
                <h3 className="font-bold text-slate-800 mb-3 flex items-center gap-2">
                  <Gift className="w-4 h-4 text-purple-500" />
                  Liste des gagnants
                </h3>
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-100">
                      <tr>
                        <th className="text-left p-3 font-medium text-slate-700">Rang</th>
                        <th className="text-left p-3 font-medium text-slate-700">Gagnant</th>
                        {draw.custom_field_label && (
                          <th className="text-left p-3 font-medium text-slate-700">{draw.custom_field_label}</th>
                        )}
                        <th className="text-left p-3 font-medium text-slate-700">Référence</th>
                        <th className="text-left p-3 font-medium text-slate-700">Lot remporté</th>
                      </tr>
                    </thead>
                    <tbody>
                      {draw.results?.map((result, idx) => (
                        <tr key={idx} className="border-t">
                          <td className="p-3 font-bold text-blue-600">{idx + 1}</td>
                          <td className="p-3 text-slate-800 font-medium">{result.participant?.name || result.participant_name || "N/A"}</td>
                          {draw.custom_field_label && (
                            <td className="p-3 text-slate-600">{result.participant?.custom_field_value || result.participant_custom_field || "-"}</td>
                          )}
                          <td className="p-3 font-mono text-xs text-slate-500">{result.participant?.reference || result.participant_reference || ""}</td>
                          <td className="p-3 text-slate-800">{result.prize?.name || result.prize_name || "N/A"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Attestation */}
              <div className="bg-slate-50 rounded-lg p-4 border">
                <p className="text-sm text-slate-700">
                  Je soussigné(e), <strong>{certificateData.supervisor_name || "___________________"}</strong>, 
                  {certificateData.supervisor_type === "huissier" ? " Huissier de Justice" : " en qualité de contrôleur"},
                  {certificateData.supervisor_role && ` (${certificateData.supervisor_role})`}, 
                  atteste avoir assisté au tirage au sort ci-dessus mentionné et certifie que celui-ci 
                  s'est déroulé dans des conditions de régularité et de transparence satisfaisantes.
                </p>
                {certificateData.notes && (
                  <p className="text-sm text-slate-600 mt-2 italic">
                    Observations : {certificateData.notes}
                  </p>
                )}
              </div>

              {/* Signature Section */}
              <div className="grid grid-cols-2 gap-8 pt-4 border-t">
                <div>
                  <p className="text-sm text-slate-600 mb-2">
                    Fait à _____________________, le {certificateData.supervisor_date ? new Date(certificateData.supervisor_date).toLocaleDateString('fr-FR') : "___/___/____"} 
                    à {certificateData.supervisor_time || "__:__"}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-700 mb-2">
                    Signature du {certificateData.supervisor_type === "huissier" ? "Huissier" : "Contrôleur"} :
                  </p>
                  {certificateData.signature ? (
                    <img 
                      src={certificateData.signature} 
                      alt="Signature" 
                      className="h-20 border-b-2 border-slate-300"
                    />
                  ) : (
                    <div className="h-20 border-b-2 border-dashed border-slate-300" />
                  )}
                  <p className="text-xs text-slate-500 mt-1">{certificateData.supervisor_name || "Nom du signataire"}</p>
                </div>
              </div>

              {/* Footer */}
              <div className="text-center pt-4 border-t text-xs text-slate-400">
                <p>Document généré automatiquement par TIREO le {new Date().toLocaleDateString('fr-FR')} à {new Date().toLocaleTimeString('fr-FR')}</p>
                <p>Ce document atteste du bon déroulement du tirage au sort et peut être produit en cas de contestation.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
